import { useMemo } from "react";
import {
  SystemEntityCollections,
  SystemEntityProofreadingStatus,
} from "../../../shared/enums/shared-enums";
import { initMetadata } from "../../../controllers/formController";
import { Person } from "../../../types/person";
import { Version } from "../../../types/version";
import {
  ReferenceVersionConnection,
  SystemVersionConnectionDirection,
  SystemVersionReferenceToEntity,
} from "../../../types/referenceVersion";

const getEntityCollection = (
  entity: Version | Person
): SystemEntityCollections =>
  "username" in entity
    ? SystemEntityCollections.PERSON
    : SystemEntityCollections.VERSION;

const useReferenceVersionLogic = (
  from: Version | null,
  to: Version | Person | null,
  referenceToEntity: SystemVersionReferenceToEntity | null = null,
  connectionDirection: SystemVersionConnectionDirection | null = null
) => {
  const metadataFieldNames = useMemo<Array<keyof ReferenceVersionConnection>>(
    () => [
      "system_reference_to_entity",
      "version_reference_type",
      "system_referring_to_version_connection_type",
    ],
    []
  );

  const defaultReferenceVersion = useMemo<ReferenceVersionConnection>(() => {
    return {
      _from: from?._id || "",
      _to: to?._id || "",
      _id: "",
      _key: "",
      _rev: "",
      system_reference_to_entity: referenceToEntity,
      version_reference_type: null,
      system_referring_to_version_connection_type: connectionDirection,
      fromDoc: from
        ? { ...from, _collection: SystemEntityCollections.VERSION }
        : null,
      toDoc: to ? { ...to, _collection: getEntityCollection(to) } : null,
      attributes_metadata:
        initMetadata<ReferenceVersionConnection>(metadataFieldNames),
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
      internal_notes: "",
      notes: "",
    };
  }, [from, to, metadataFieldNames, referenceToEntity, connectionDirection]);

  const getDisplayName = (connection: ReferenceVersionConnection) => {
    const direction = connection
      ? connection.system_referring_to_version_connection_type
      : connectionDirection;
    const entity = connection
      ? connection.system_reference_to_entity
      : referenceToEntity;
    const entityLabel =
      entity === "person" ? "Person" : entity === "version" ? "Version" : "Unknown";
    return direction === "referenced_by"
      ? `Referenced by ${entityLabel}`
      : `Reference to ${entityLabel}`;
  };

  return {
    defaultReferenceVersion,
    metadataFieldNames,
    getDisplayName,
  };
};

export default useReferenceVersionLogic;
